import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { TrackingEvents, TrackingTabs, useTrack } from '../../tracking';
import { Platforms } from '@/common/constants';
import { usePlatform } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import type { Option, TranslationKey } from '@/common/types';
import { FilterChipGroup, Knockout } from '@/ui/components';
import type { BracketCardMatch } from '@/ui/components/BracketCard/BracketCard.types';
import type { MatchGroup, Rounds } from '@/ui/components/Knockout/Knockout.types';
import { usePrematchData } from '../../hooks';
import { mapKnockoutMatchToBracketCardMatch } from '../../utils/mapKnockoutMatchToBracketCardMatch';

const KNOCKOUT_ROUND_TRANSLATION_KEYS: Record<string, TranslationKey> = {
  final: 'PreMatchStats.Stage.Final',
  semifinal: 'PreMatchStats.Stage.SemiFinal',
  quarterfinal: 'PreMatchStats.Stage.QuarterFinal',
  round_of_16: 'PreMatchStats.Stage.RoundOf16',
  round_of_32: 'PreMatchStats.Stage.RoundOf32',
  round_of_64: 'PreMatchStats.Stage.RoundOf64',
  round_1: 'PreMatchStats.Round1',
  round_2: 'PreMatchStats.Round2',
  round_3: 'PreMatchStats.Round3',
  round_4: 'PreMatchStats.Round4',
  round_5: 'PreMatchStats.Round5',
  round_6: 'PreMatchStats.Round6',
  round_7: 'PreMatchStats.Round7',
  round_8: 'PreMatchStats.Round8',
};

interface KnockoutParseResult {
  rounds: Rounds;
  finalMatch?: BracketCardMatch;
  thirdPlaceMatch?: BracketCardMatch;
  currentRoundIndex: number;
}

export default function KnockoutView() {
  const [roundIndex, setRoundIndex] = useState<number>();

  const containerRef = useRef<HTMLDivElement>(null);

  const translate = useNewTranslations();
  const { table } = usePrematchData();
  const platform = usePlatform();
  const track = useTrack();

  useEffect(() => {
    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    containerElement.scrollTo({ top: 0, behavior: 'smooth' });
  }, [roundIndex]);

  if (!table?.knockout) {
    return null;
  }

  // Reverse knockout because API is sorted from final to lower stages while Knockout component in reverse.
  const knockout = [...table.knockout].reverse();

  const { rounds, finalMatch, thirdPlaceMatch, currentRoundIndex } =
    knockout.reduce<KnockoutParseResult>(
      (acc, stage) => {
        if (stage.matches.length === 0) {
          return acc;
        }

        if (stage.stage_type === 'final') {
          acc.finalMatch = mapKnockoutMatchToBracketCardMatch(stage.matches[0]);
          return acc;
        }

        if (stage.stage_type === 'third_place') {
          acc.thirdPlaceMatch = mapKnockoutMatchToBracketCardMatch(stage.matches[0]);
          return acc;
        }

        const bracketCardMatches = stage.matches.map(mapKnockoutMatchToBracketCardMatch);
        const matchGroups: MatchGroup[] = [];

        for (let i = 0; i < bracketCardMatches.length; i += 2) {
          matchGroups.push([bracketCardMatches[i], bracketCardMatches[i + 1]]);
        }

        acc.rounds.push(matchGroups);

        if (stage.is_current_stage) {
          acc.currentRoundIndex = acc.rounds.length - 1;
        }

        return acc;
      },
      { rounds: [], currentRoundIndex: 0 },
    );

  const roundsLabels = knockout
    .filter((stage) => stage.stage_type !== 'third_place')
    .map(
      (item, index): Option => ({
        value: String(index),
        label: translate(KNOCKOUT_ROUND_TRANSLATION_KEYS[item.stage_type] ?? item.stage_type),
      }),
    );

  const stageTypeByRoundIndex = knockout
    .filter((stage) => stage.stage_type !== 'third_place')
    .map((stage) => stage.stage_type);

  const finalRoundIndex = roundIndex ?? currentRoundIndex;

  const handleRoundIndexChange = (value: string) => {
    const index = Number(value);
    const stageType = stageTypeByRoundIndex[index];
    const elementOrder = index + 1;
    if (stageType) {
      track(TrackingEvents.FilterClick, {
        tab: TrackingTabs.Table,
        filter: stageType,
        elementOrder,
      });
    }
    setRoundIndex(index);
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex flex-col gap-3 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
        {
          'pt-6 pb-3': platform === Platforms.MOBILE,
          'pt-3': platform !== Platforms.MOBILE,
        },
      )}
    >
      <FilterChipGroup
        className={clsx(platform === Platforms.MOBILE && 'px-3 shrink-0')}
        options={roundsLabels}
        value={String(finalRoundIndex)}
        platform={platform}
        onValueChange={handleRoundIndexChange}
        isTogglable={false}
      />
      <div className={clsx(finalRoundIndex === 0 && platform === Platforms.MOBILE && 'px-3')}>
        <Knockout
          rounds={rounds}
          finalMatch={finalMatch}
          thirdPlaceMatch={thirdPlaceMatch}
          thirdPlaceLabel={translate('PreMatchStats.ThirdPlace')}
          roundIndex={finalRoundIndex}
          onRoundIndexChange={setRoundIndex}
        />
      </div>
    </div>
  );
}
